import { Dispatch, FC, ReactNode, SetStateAction } from "react";
import { Switch } from "@headlessui/react";
import { classNames } from "../../utils/class-names";
import { Button } from "../button";

export interface ToggleProps {
  checked: boolean;
  setChecked: Dispatch<SetStateAction<boolean>>;
  checkedLabel: ReactNode;
  uncheckedLabel: ReactNode;
  disabled?: boolean;
  warning?: boolean;
}

const switchClassMap = {
  unchecked: {
    all: "bg-shale-800",
    enabled: "border-blue-300",
    disabled: "border-shale-400",
    warning: "!border-amber-300",
  },
  checked: {
    all: "",
    enabled: "border-blue-300 bg-blue-300",
    disabled: "border-shale-400 bg-shale-400",
    warning: "!border-amber-300 bg-amber-300",
  },
};

const dotClassMap = {
  unchecked: {
    all: "translate-x-[2px] translate-y-[2px]",
    enabled: "bg-blue-300",
    disabled: "bg-shale-400",
    warning: "!bg-amber-300",
  },
  checked: {
    all: "bg-shale-800 translate-x-[13px] translate-y-[2px]",
    enabled: "",
    disabled: "",
    warning: "",
  },
};

export const Toggle: FC<ToggleProps> = ({
  checked,
  setChecked,
  disabled = false,
  checkedLabel,
  uncheckedLabel,
  warning,
}) => {
  const key1 = checked ? "checked" : "unchecked";
  const key2 = disabled ? "disabled" : "enabled";

  const switchClassName = classNames(
    "border-2 relative inline-flex w-[22px] h-3 shrink-0 rounded-full transition-colors duration-300 ease-in-out focus:outline-none focus-visible:ring-2  focus-visible:ring-slate-100 focus-visible:ring-opacity-75",
    switchClassMap[key1]["all"],
    switchClassMap[key1][key2],
    warning ? switchClassMap[key1].warning : ""
  );

  const dotClassName = classNames(
    "inline-block h-1 w-1 transform rounded-full ring-0 transition duration-300 ease-in-out",
    dotClassMap[key1]["all"],
    dotClassMap[key1][key2],
    warning ? dotClassMap[key1].warning : ""
  );

  return (
    <Button
      className="flex gap-2.5 items-center"
      variant="action"
      size="small"
      onClick={() => setChecked((val) => !val)}
      disabled={disabled}
    >
      <span
        className={`text-base font-mono ${
          disabled
            ? "text-shale-400"
            : warning
            ? "text-amber-300"
            : "text-white"
        }`}
      >
        {checked ? checkedLabel : uncheckedLabel}
      </span>
      <Switch checked={checked} disabled={disabled} className={switchClassName}>
        <span aria-hidden="true" className={dotClassName} />
      </Switch>
    </Button>
  );
};
